import type { CSSProperties } from 'react';
import type { FontPair, PublicTheme } from './types';

/** Luminância relativa WCAG de uma cor #rrggbb. */
export function luminance(hex: string): number {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return 0;
  const n = parseInt(m[1]!, 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0]! + 0.7152 * ch[1]! + 0.0722 * ch[2]!;
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

const INK_LIGHT = '#f4f6f4';
const INK_DARK = '#17201f';

/** Texto legível sobre uma cor de fundo (escolhe o de maior contraste). */
export function readableOn(bg: string): string {
  return contrast(bg, INK_DARK) >= contrast(bg, INK_LIGHT) ? INK_DARK : INK_LIGHT;
}

export const FONT_PAIRS: Record<FontPair, { label: string; sample: string; display: string; body: string; description: string; weight: number }> = {
  classic: {
    weight: 400,
    label: 'Clássica',
    description: 'Serifa acolhedora nos títulos, texto limpo.',
    sample: 'Peças com história',
    display: "'Young Serif', Georgia, 'Times New Roman', serif",
    body: "'Figtree Variable', ui-sans-serif, system-ui, sans-serif",
  },
  modern: {
    weight: 560,
    label: 'Moderna',
    description: 'Geométrica, leve e direta.',
    sample: 'Peças com história',
    display: "'Outfit Variable', ui-sans-serif, system-ui, sans-serif",
    body: "'Figtree Variable', ui-sans-serif, system-ui, sans-serif",
  },
  editorial: {
    weight: 600,
    label: 'Editorial',
    description: 'Serifa de contraste alto, ar de revista.',
    sample: 'Peças com história',
    display: "'Playfair Display Variable', Georgia, serif",
    body: "'Figtree Variable', ui-sans-serif, system-ui, sans-serif",
  },
};

/** Carrega só as fontes do par escolhido (sob demanda, fora do bundle principal). */
export async function loadSiteFonts(pair: FontPair): Promise<void> {
  try {
    await import('@fontsource-variable/figtree/index.css');
    if (pair === 'classic') await import('@fontsource/young-serif/index.css');
    else if (pair === 'modern') await import('@fontsource-variable/outfit/index.css');
    else await import('@fontsource-variable/playfair-display/index.css');
  } catch {
    /* sem fonte personalizada, cai no fallback do sistema */
  }
}

/** Presets de paleta sugeridos no editor de tema (todos com contraste AA para texto e botões). */
export const THEME_PRESETS: { name: string; primary: string; accent: string; background: string }[] = [
  { name: 'Água-marinha', primary: '#0f5e63', accent: '#e8a33d', background: '#f3f8f6' },
  { name: 'Sálvia', primary: '#3d5a45', accent: '#d9825b', background: '#f5f4ee' },
  { name: 'Azul-hortênsia', primary: '#2f4f8f', accent: '#f0b24a', background: '#f4f6fb' },
  { name: 'Ameixa', primary: '#5b2a56', accent: '#f08a7e', background: '#faf5f8' },
  { name: 'Coral', primary: '#b3402f', accent: '#1f6f78', background: '#fdf6f2' },
];

/** Variáveis CSS do tema da loja, aplicadas no elemento raiz do site (nunca em <body>). */
export function themeStyle(theme: Pick<PublicTheme, 'primary' | 'accent' | 'background' | 'fontPair'>): CSSProperties {
  const bg = theme.background;
  const darkBg = luminance(bg) < 0.18;
  const base = darkBg ? INK_LIGHT : INK_DARK;
  const fonts = FONT_PAIRS[theme.fontPair] ?? FONT_PAIRS.classic;
  // texto de destaque (links/títulos coloridos) precisa de contraste com o fundo; senão usa a tinta
  const primaryText = contrast(theme.primary, bg) >= 4.5 ? theme.primary : base;
  const vars: Record<string, string> = {
    '--s-bg': bg,
    '--s-primary': theme.primary,
    '--s-accent': theme.accent,
    '--s-on-primary': readableOn(theme.primary),
    '--s-on-accent': readableOn(theme.accent),
    '--s-primary-text': primaryText,
    '--s-ink': `color-mix(in oklab, ${base} 90%, ${theme.primary})`,
    '--s-ink-soft': `color-mix(in oklab, ${base} 68%, ${bg})`,
    '--s-surface': `color-mix(in oklab, ${bg} 90%, ${theme.primary} 10%)`,
    '--s-tint': `color-mix(in oklab, ${bg} 80%, ${theme.accent} 20%)`,
    '--s-line': `color-mix(in oklab, ${bg} 82%, ${base})`,
    '--s-font-display': fonts.display,
    '--s-font-body': fonts.body,
    '--s-display-weight': String(fonts.weight),
  };
  return vars as CSSProperties;
}
